import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Modal,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { mockRoles } from "../data/mockData";
import {
  createUser,
  fetchClientDropDownList,
  fetchEntityDropDownList,
} from "../services/api";
import { getCurrentUser } from "../services/authService";
import { USER_ROLES } from "../lib/utils/constant";

function AddUser() {
  const navigate = useNavigate();

  // --- FIX: Stabilize currentUser to prevent infinite useEffect loops ---
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setCurrentUser(user);
    } else {
      navigate("/login");
    }
  }, [navigate]);
  // ---------------------------------------------------------------------

  const [userType, setUserType] = useState("platform");
  const [formData, setFormData] = useState({
    entity: "",
    client: "",
    role: "",
    firstName: "",
    lastName: "",
    email: "",
  });

  const [entitiesList, setEntitiesList] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for saving

  // --- Dynamic Visibility State ---
  const [isEntityLocked, setIsEntityLocked] = useState(false);
  const [isClientLocked, setIsClientLocked] = useState(false);
  // --- NEW: State for filtered roles ---
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  const isPlatformVisible = currentUser?.role?.code === USER_ROLES.SYS_ADMIN;
  const isEntityAccount =
    currentUser?.entityMaster?.accountType === USER_ROLES.ENTITY;
  const isClientAccount =
    currentUser?.entityMaster?.accountType === USER_ROLES.CLIENT;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === "email") setEmailError("");
  };

  const handleUserTypeChange = (e) => {
    const nextUserType = e.target.value;
    const accountType = currentUser?.entityMaster?.accountType;
    const userEntityId = currentUser?.entityMaster?.id;

    setUserType(nextUserType);
    setFormData((prev) => {
      const next = { ...prev };

      // Keep entity prefilled for entity accounts on non-platform user creation.
      if (accountType === USER_ROLES.ENTITY && nextUserType !== "platform") {
        next.entity = userEntityId ? String(userEntityId) : "";
      } else if (accountType === USER_ROLES.CLIENT && nextUserType !== "platform") {
        next.client = userEntityId ? String(userEntityId) : "";
      } else if (nextUserType === "platform" && !isEntityLocked) {
        next.entity = "";
      }

      if (nextUserType !== "client" && !isClientLocked) {
        next.client = "";
      }

      return next;
    });
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setEmailError("");

    // Email Validation
    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    const targetEntityId =
      userType === "client"
        ? formData.client
        : userType === "entity"
          ? formData.entity
          : null;

    if (userType !== "platform" && !targetEntityId) {
      setErrorMessage("Please select required fields before saving.");
      setShowErrorModal(true);
      return;
    }

    const newUser = {
      emailAddress: formData.email,
      entityMasterId: targetEntityId ? parseInt(targetEntityId) : null,
      firstName: formData.firstName,
      lastName: formData.lastName,
      roleId: parseInt(formData.role),
    };

    try {
      await createUser(newUser);
      setShowSuccessModal(true);
      //navigate('/users');
    } catch (err) {
      console.error("Failed to create user:", err);
      setErrorMessage(
        err.response?.data?.message ||
          "Failed to create user. Please check your inputs.",
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate("/users");
  };

  useEffect(() => {
    const fetchEntities = async () => {
      const response = await fetchEntityDropDownList();
      setEntitiesList(response?.data?.data?.items);
    };

    if (isEntityAccount) {
      const userEntityId = currentUser?.entityMaster?.id;
      const userEntityName =
        currentUser?.entityMaster?.name || currentUser?.entityMaster?.entityName;

      if (userEntityId) {
        setEntitiesList([{ id: userEntityId, name: userEntityName || "Entity" }]);
      } else {
        setEntitiesList([]);
      }
      return;
    }

    if (isClientAccount) {
      setEntitiesList([]);
      return;
    }

    if (userType === "entity" || userType === "client") {
      fetchEntities();
    }
  }, [userType, isEntityAccount, isClientAccount, currentUser]);

  useEffect(() => {
    const fetchClients = async () => {
      const response = await fetchClientDropDownList(parseInt(formData.entity));
      setAvailableClients(response?.data?.data?.items);
    };

    if (isClientAccount) {
      const userClientId = currentUser?.entityMaster?.id;
      const userClientName =
        currentUser?.entityMaster?.name || currentUser?.entityMaster?.entityName;
      if (userClientId) {
        setAvailableClients([{ id: userClientId, name: userClientName || "Client" }]);
      } else {
        setAvailableClients([]);
      }
      return;
    }

    if (formData.entity && userType === "client") {
      fetchClients();
    }
  }, [formData.entity, userType, isClientAccount, currentUser]);


  useEffect(() => {
    let rolesData = [];

    switch (userType) {
      case "platform":
        rolesData = mockRoles.filter((r) => r.id === 2);
        break;
      case "entity":
        rolesData = mockRoles.filter((r) => r.id !== 2 && r.id !== 6);
        break;
      case "client":
        rolesData = mockRoles.filter((r) => r.id !== 2);
        break;
      default:
        rolesData = [];
        break;
    }

    // Entity account users should not be allowed to create Case Manager users.
    if (isEntityAccount) {
      rolesData = rolesData.filter((r) => r.id !== 6);
    }

    setFilteredRoles(rolesData);

    // Reset selected role if it is not valid for the selected user type.
    setFormData((prev) =>
      rolesData.some((role) => String(role.id) === String(prev.role))
        ? prev
        : { ...prev, role: "" },
    );
  }, [userType, isEntityAccount]);
  // Wait for user data to load before rendering form to avoid flashes
  useEffect(() => {
    const userEntityId = currentUser?.entityMaster?.id;
    const prefilledEntity = userEntityId ? String(userEntityId) : "";

    if (isEntityAccount) {
      setUserType("entity");
      setFormData((prev) =>
        prev.entity === prefilledEntity ? prev : { ...prev, entity: prefilledEntity },
      );
      setIsEntityLocked(true);
      setIsClientLocked(false);
      return;
    }

    if (isClientAccount) {
      setUserType("client");
      setFormData((prev) =>
        prev.client === prefilledEntity ? prev : { ...prev, client: prefilledEntity },
      );
      setIsEntityLocked(true);
      setIsClientLocked(true);
      return;
    }

    setIsEntityLocked(false);
    setIsClientLocked(false);
  }, [isEntityAccount, isClientAccount, currentUser]);
  if (!currentUser) return null;
  return (
    <Container fluid className="px-0">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white p-3">
              <Row className="justify-content-between align-items-center">
                <Col>
                  <h1 className="h3 mb-0 text-primary">CREATE ADMIN USER</h1>
                </Col>
                {/* <Col className="text-end"><span className="text-danger">*required fields</span></Col> */}
              </Row>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              <Form onSubmit={handleSubmit}>
                {/* --- Dynamic Radio Buttons --- */}
                <Form.Group as={Row} className="mb-3">
                  <Col sm={10}>
                    {isPlatformVisible && (
                      <Form.Check
                        type="radio"
                        label="Platform"
                        name="userType"
                        value="platform"
                        checked={userType === "platform"}
                        onChange={handleUserTypeChange}
                        inline
                      />
                    )}
                    {!isClientAccount  && (
                      <Form.Check
                        type="radio"
                        label="Entity"
                        name="userType"
                        value="entity"
                        checked={userType === "entity"}
                        onChange={handleUserTypeChange}
                        inline
                      />
                    )}
                    {!isClientAccount && (
                      <Form.Check
                        type="radio"
                        label="Client of an entity"
                        name="userType"
                        value="client"
                        checked={userType === "client"}
                        onChange={handleUserTypeChange}
                        inline
                      />
                    )}
                  </Col>
                </Form.Group>
                {userType !== "platform" && (
                    <Row className="mb-3">
                      {/* Entity Dropdown */}
                      {!isClientAccount && (
                        <Form.Group as={Col} md={6} controlId="entity">
                          <Form.Label>ENTITY*</Form.Label>
                          <Form.Select
                            required
                            name="entity"
                            value={formData.entity}
                            onChange={handleChange}
                            disabled={isEntityLocked}
                          >
                            <option value="">Select...</option>
                            {entitiesList?.map((e) => (
                              <option key={e.id} value={e.id}>
                                {e.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}

                      {/* Client Dropdown (Only show if userType is client) */}
                      {userType === "client" && (
                        <Form.Group
                          as={Col}
                          md={6}
                          controlId="client"
                        >
                          <Form.Label>CLIENT*</Form.Label>
                          <Form.Select
                            required
                            name="client"
                            value={formData.client}
                            onChange={handleChange}
                            disabled={isClientAccount || (!formData.entity && !isClientAccount) || isClientLocked}
                          >
                            <option value="">Select...</option>
                            {availableClients.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                    </Row>
                  )}

                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="role">
                    <Form.Label>ROLE*</Form.Label>
                    <Form.Select
                      required
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                    >
                      <option value="">Select...</option>
                      {/* --- UPDATED: Use filteredRoles instead of mockRoles --- */}
                      {filteredRoles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="firstName">
                    <Form.Label>FIRST NAME*</Form.Label>
                    <Form.Control
                      required
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Enter First Name"
                    />
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="lastName">
                    <Form.Label>LAST NAME*</Form.Label>
                    <Form.Control
                      required
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Enter Last Name"
                    />
                  </Form.Group>
                </Row>
                <Row className="mb-4">
                  <Form.Group as={Col} md={6} controlId="email">
                    <Form.Label>EMAIL*</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                      isInvalid={!!emailError}
                    />
                    <Form.Control.Feedback type="invalid">
                      {emailError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                <div className="d-flex justify-content-center gap-2">
                  <Button as={Link} to="/users" variant="secondary">
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    className="text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- Modals --- */}
      <Modal
        show={showSuccessModal}
        onHide={handleSuccessClose}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-success">Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>User created successfully!</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleSuccessClose}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
export default AddUser;
